import { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { ArrowLeft, MapPin, Clock, Users, TrendingDown, Map as MapIcon, Image as ImageIcon, Navigation, Store as StoreIcon, ThumbsUp, ThumbsDown, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const SORT_OPTIONS = [
  { value: 'price', label: 'Cheapest first' },
  { value: 'last_reported', label: 'Last reported' },
  { value: 'popularity', label: 'Popular stores' },
];
const NEAR_ME_RADIUS_KM = 25;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function Results() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const { selectedCity, userLocation, setUserLocation, token } = useContext(AuthContext);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [voteCache, setVoteCache] = useState(() => {
    try {
      const raw = localStorage.getItem('reportVotes');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [confirmCache, setConfirmCache] = useState(() => {
    try {
      const raw = localStorage.getItem('reportConfirms');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [sortBy, setSortBy] = useState('price');
  const [storeFilter, setStoreFilter] = useState('');
  const [nearMe, setNearMe] = useState(!!userLocation);
  const [locationLoading, setLocationLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'

  const goToReport = (result) => {
    const params = new URLSearchParams({
      product_name: result.product_name,
      store_name: result.store_name,
      city: selectedCity?.id || result.city || '',
      lat: String(result.latitude),
      lng: String(result.longitude),
    });
    if (result.brand) params.set('brand', result.brand);
    if (result.size) params.set('size', result.size);
    navigate(`/report?${params.toString()}`);
  };

  const fetchResults = async (lat = null, lng = null, radius = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        product: query || '',
        city: selectedCity?.id || '',
        sort_by: sortBy,
      });
      if (storeFilter.trim()) params.set('store', storeFilter.trim());
      if (lat != null && lng != null) {
        params.set('lat', String(lat));
        params.set('lng', String(lng));
        params.set('radius', String(radius ?? NEAR_ME_RADIUS_KM));
      } else if (selectedCity?.lat != null && selectedCity?.lng != null) {
        params.set('lat', String(selectedCity.lat));
        params.set('lng', String(selectedCity.lng));
      }
      const response = await fetch(`${API}/search?${params}`);
      const data = await response.json();
      setResults(data);
    } catch (error) {
      console.error('Failed to fetch results', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCity) {
      navigate('/');
      return;
    }
    const lat = nearMe && userLocation ? userLocation.lat : null;
    const lng = nearMe && userLocation ? userLocation.lng : null;
    fetchResults(lat, lng, NEAR_ME_RADIUS_KM);
    // `fetchResults` depends only on stable API + current state; including it would cause unnecessary re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedCity?.id, sortBy, storeFilter, nearMe, userLocation?.lat, userLocation?.lng, navigate]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!selectedStore) {
        setPriceHistory([]);
        return;
      }
      try {
        setLoadingHistory(true);
        const params = new URLSearchParams({
          store_id: selectedStore.store_id,
          product_name: selectedStore.product_name,
          ...(selectedStore.brand ? { brand: selectedStore.brand } : {}),
          ...(selectedStore.size ? { size: selectedStore.size } : {}),
          limit: '10',
        });
        const response = await fetch(`${API}/insights/price-history?${params.toString()}`);
        const data = await response.json();
        if (Array.isArray(data)) {
          const withVotes = data.map((entry) => ({
            ...entry,
            my_vote: voteCache[entry.id] ?? undefined,
            my_confirmed: confirmCache[entry.id] ?? undefined,
          }));
          setPriceHistory(withVotes);
        } else {
          setPriceHistory([]);
        }
      } catch (error) {
        console.error('Failed to fetch price history', error);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
    // We map history using the latest vote/confirm/thanks caches when selectedStore changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore, voteCache, confirmCache]);

  const flagReport = async (entryId, value) => {
    if (!token) {
      toast.error('Please sign in to rate reports');
      return;
    }
    try {
      const response = await fetch(`${API}/reports/${entryId}/flag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ value }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Could not submit feedback');
        return;
      }
      setVoteCache((prev) => {
        const updated = { ...prev, [entryId]: data.my_vote };
        try {
          localStorage.setItem('reportVotes', JSON.stringify(updated));
        } catch {
          // ignore storage errors
        }
        return updated;
      });
      setPriceHistory((prev) =>
        prev.map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                flags_up: data.flags_up,
                flags_down: data.flags_down,
                my_vote: data.my_vote,
              }
            : entry
        )
      );
    } catch (error) {
      console.error('Failed to flag report', error);
      toast.error('Connection error while sending feedback');
    }
  };

  const confirmReport = async (entryId) => {
    if (!token) {
      toast.error('Please sign in to confirm prices');
      return;
    }
    try {
      const response = await fetch(`${API}/reports/${entryId}/confirm`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Could not confirm price');
        return;
      }
      setConfirmCache((prev) => {
        const updated = { ...prev, [entryId]: data.confirmed };
        try {
          localStorage.setItem('reportConfirms', JSON.stringify(updated));
        } catch {
          // ignore storage errors
        }
        return updated;
      });
      setPriceHistory((prev) =>
        prev.map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                confirmations: data.confirmations,
                my_confirmed: data.confirmed,
              }
            : entry
        )
      );
    } catch (error) {
      console.error('Failed to confirm report', error);
      toast.error('Connection error while confirming price');
    }
  };


  const handleNearMe = () => {
    if (userLocation) {
      setNearMe(true);
      return;
    }
    setLocationLoading(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setLocationLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setNearMe(true);
        toast.success('Using your location for "Near Me" results');
        setLocationLoading(false);
      },
      () => {
        toast.error('Could not get your location. Using city center.');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/search')}
            variant="ghost"
            size="icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-heading font-bold text-primary">Results</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
            Prices for "{query}"
          </h2>
          <p className="text-muted-foreground flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4" />
            {selectedCity?.name} • {results.length} results
            {nearMe && userLocation && (
              <Badge variant="secondary" className="ml-2">Near Me ({NEAR_ME_RADIUS_KM} km)</Badge>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              data-testid="near-me-button"
              variant={nearMe && userLocation ? 'default' : 'outline'}
              size="sm"
              onClick={handleNearMe}
              disabled={locationLoading}
              className="rounded-full"
            >
              <Navigation className="w-4 h-4 mr-2" />
              {locationLoading ? 'Getting location...' : nearMe && userLocation ? 'Near Me on' : 'Near Me'}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Sort:</span>
              <select
                data-testid="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[140px]">
              <StoreIcon className="w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="store-filter-input"
                placeholder="Filter by store..."
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="max-w-[180px] h-9 text-sm"
              />
            </div>
            <div className="flex items-center gap-1 border border-border rounded-full px-1 py-1 bg-card">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 text-xs sm:text-sm rounded-full ${
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground'
                }`}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('map')}
                className={`px-3 py-1 text-xs sm:text-sm rounded-full ${
                  viewMode === 'map'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground'
                }`}
              >
                Map
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            <p className="mt-4 text-muted-foreground">Searching for best prices...</p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-muted/50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <TrendingDown className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              No prices found
            </h3>
            <p className="text-muted-foreground mb-6">
              Be the first to report a price for {query}!
            </p>
            <Button
              data-testid="report-first-price-button"
              onClick={() => navigate('/report')}
              className="rounded-full"
            >
              Report Price
            </Button>
          </div>
        ) : viewMode === 'list' ? (
          <div className="grid grid-cols-1 gap-3">
            {results.map((result, idx) => (
              <Card
                key={idx}
                data-testid={`result-card-${idx}`}
                className="p-4 hover:shadow-md transition-all duration-300 cursor-pointer hover-lift"
                onClick={() => setSelectedStore(result)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-heading font-bold text-lg text-foreground">
                          {result.store_name}
                        </h3>
                        <div className="mt-1">
                          <p className="text-base font-medium text-foreground">
                            {result.product_name}
                            {result.brand && (
                              <span className="text-muted-foreground"> • {result.brand}</span>
                            )}
                            {result.size && (
                              <span className="text-muted-foreground font-normal text-sm"> ({result.size})</span>
                            )}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {result.distance_km ? `${result.distance_km} km away` : result.city}
                        </p>
                      </div>
                      <div className="text-right space-y-1">
                        <div className="text-2xl font-heading font-bold text-primary tabular-nums">
                          R{(result.has_active_combo && result.deal_unit_price != null
                            ? result.deal_unit_price
                            : result.has_active_promo && result.promo_price != null
                            ? result.promo_price
                            : result.price
                          ).toFixed(2)}
                        </div>
                        {result.has_active_combo && result.deal_label && (
                          <div className="text-xs text-accent font-medium">
                            {result.deal_label}
                            {result.deal_unit_price != null && (
                              <span className="text-muted-foreground ml-1">
                                (R{result.deal_unit_price.toFixed(2)} each)
                              </span>
                            )}
                          </div>
                        )}
                        {result.has_active_promo && result.promo_price && !result.has_active_combo && (
                          <div className="text-xs text-accent font-medium">
                            Promo: R{result.promo_price.toFixed(2)}
                            {result.promo_valid_to &&
                              ` until ${new Date(result.promo_valid_to).toLocaleDateString()}`}
                          </div>
                        )}
                        {!result.has_active_promo && result.has_ended_promo && result.promo_ended_at && (
                          <div className="text-xs text-muted-foreground">
                            Promo ended {new Date(result.promo_ended_at).toLocaleDateString()}
                          </div>
                        )}
                        {result.confirmations > 0 && (
                          <div className="text-[11px] text-muted-foreground">
                            ✔ Confirmed by {result.confirmations}{' '}
                            {result.confirmations === 1 ? 'shopper' : 'shoppers'}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {result.report_count} {result.report_count === 1 ? 'report' : 'reports'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(result.last_reported)}
                      </span>
                      {result.confidence_level && (
                        <span className="flex items-center gap-1">
                          <Badge
                            variant={
                              result.confidence_level === 'high'
                                ? 'default'
                                : result.confidence_level === 'medium'
                                ? 'secondary'
                                : 'outline'
                            }
                            className="text-xs"
                          >
                            {result.confidence_level === 'high'
                              ? 'High confidence'
                              : result.confidence_level === 'medium'
                              ? 'Medium confidence'
                              : 'Low confidence'}
                          </Badge>
                        </span>
                      )}
                      {result.suspicious && (
                        <span className="text-xs text-destructive">
                          Possibly incorrect price (outlier)
                        </span>
                      )}
                    </div>

                    {result.reporters && result.reporters.length > 0 && (
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex -space-x-2">
                          {result.reporters.slice(0, 3).map((reporter, i) => (
                            <Avatar key={i} className="w-8 h-8 border-2 border-background">
                              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                {reporter.name?.charAt(0).toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Reported by {result.reporters[0]?.name}
                          {result.reporters.length > 1 && ` +${result.reporters.length - 1}`}
                        </span>
                      </div>
                    )}

                    <div className="mt-4 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToReport(result);
                        }}
                      >
                        Update price
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-border h-[480px] relative z-10">
            <MapContainer
              center={
                results.length > 0
                  ? [results[0].latitude, results[0].longitude]
                  : selectedCity
                  ? [selectedCity.lat, selectedCity.lng]
                  : [0, 0]
              }
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              {results.map((result, idx) => {
                const effectivePrice =
                  result.has_active_combo && result.deal_unit_price != null
                    ? result.deal_unit_price
                    : result.has_active_promo && result.promo_price
                    ? result.promo_price
                    : result.price;
                return (
                  <Marker
                    key={idx}
                    position={[result.latitude, result.longitude]}
                  >
                    <Popup>
                      <div className="space-y-1 text-xs">
                        <div className="font-heading font-semibold text-foreground">
                          {result.store_name}
                        </div>
                        <div className="text-muted-foreground">
                          {result.product_name}
                          {result.brand && ` • ${result.brand}`}
                          {result.size && ` (${result.size})`}
                        </div>
                        <div className="font-bold text-primary">
                          R{effectivePrice.toFixed(2)}
                        </div>
                        {result.has_active_combo && result.deal_label && (
                          <div className="text-[11px] text-accent">
                            {result.deal_label}
                          </div>
                        )}
                        {result.min_price != null && result.max_price != null && result.report_count > 1 && (
                          <div className="text-[11px] text-muted-foreground">
                            {result.report_count} reports • R
                            {result.min_price.toFixed(2)}–R{result.max_price.toFixed(2)} • avg R
                            {result.price.toFixed(2)}
                          </div>
                        )}
                        {result.has_active_promo && result.promo_price && (
                          <div className="text-[11px] text-accent">
                            Promo: R{result.promo_price.toFixed(2)}
                            {result.promo_valid_to &&
                              ` until ${new Date(result.promo_valid_to).toLocaleDateString()}`}
                          </div>
                        )}
                        {!result.has_active_promo && result.has_ended_promo && result.promo_ended_at && (
                          <div className="text-[11px] text-muted-foreground">
                            Promo ended {new Date(result.promo_ended_at).toLocaleDateString()}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                          {result.confidence_level && (
                            <Badge
                              variant={
                                result.confidence_level === 'high'
                                  ? 'default'
                                  : 'secondary'
                              }
                              className="text-[10px] px-2 py-0.5"
                            >
                              {result.confidence_level === 'high'
                                ? 'High confidence'
                                : result.confidence_level === 'medium'
                                ? 'Medium confidence'
                                : 'Low confidence'}
                            </Badge>
                          )}
                          {result.suspicious && (
                            <span className="text-destructive text-[10px]">
                              Outlier
                            </span>
                          )}
                          {result.distance_km && (
                            <span>
                              • {result.distance_km} km away
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-col gap-1">
                          <Button
                            size="xs"
                            className="w-full"
                            onClick={() => setSelectedStore(result)}
                          >
                            View details
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            className="w-full"
                            onClick={() => goToReport(result)}
                          >
                            Update price
                          </Button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        )}
      </main>

      {selectedStore && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-4"
          onClick={() => setSelectedStore(null)}
        >
          <div
            data-testid="store-detail-modal"
            className="bg-card rounded-t-3xl sm:rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                    {selectedStore.store_name}
                  </h3>
                  <div className="mb-3">
                    <p className="text-lg font-medium text-foreground">
                      {selectedStore.product_name}
                      {selectedStore.brand && (
                        <span className="text-muted-foreground"> • {selectedStore.brand}</span>
                      )}
                    </p>
                    {selectedStore.size && (
                      <Badge variant="secondary" className="mt-1">
                        {selectedStore.size}
                      </Badge>
                    )}
                  </div>
                  <div className="text-3xl font-heading font-bold text-primary tabular-nums">
                    R{(selectedStore.has_active_combo && selectedStore.deal_unit_price != null
                      ? selectedStore.deal_unit_price
                      : selectedStore.has_active_promo && selectedStore.promo_price != null
                      ? selectedStore.promo_price
                      : selectedStore.price
                    ).toFixed(2)}
                  </div>
                  {selectedStore.has_active_combo && selectedStore.deal_label && (
                    <div className="text-sm text-accent font-medium mt-1">
                      {selectedStore.deal_label}
                    </div>
                  )}
                </div>
                <Button
                  data-testid="close-modal-button"
                  onClick={() => setSelectedStore(null)}
                  variant="ghost"
                  size="icon"
                >
                  ✕
                </Button>
              </div>

              <div className="space-y-3 mb-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {selectedStore.distance_km
                    ? `${selectedStore.distance_km} km away`
                    : selectedStore.city}
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${selectedStore.latitude},${selectedStore.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                >
                  <Navigation className="w-4 h-4" />
                  Get directions
                </a>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  {selectedStore.report_count}{' '}
                  {selectedStore.report_count === 1 ? 'report' : 'reports'}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  Last reported {formatDate(selectedStore.last_reported)}
                </div>
              </div>

              {priceHistory.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-heading font-bold text-foreground mb-2 text-sm">
                    Recent reports
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto text-xs text-muted-foreground border border-border rounded-xl p-2 bg-muted/30">
                    {priceHistory.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2">
                        <div>
                          <span className="font-medium text-foreground">
                            R{Number(entry.price).toFixed(2)}
                          </span>
                          {entry.deal_quantity && entry.deal_price && (
                            <span className="ml-1 text-accent">
                              {entry.deal_label || `${entry.deal_quantity} for R${Number(entry.deal_price).toFixed(2)}`}
                            </span>
                          )}
                          {entry.is_promo && !entry.deal_quantity && (
                            <span className="ml-1 text-accent">
                              {entry.promo_label ? ` (${entry.promo_label})` : ' (Promo)'}
                            </span>
                          )}
                          {entry.promo_status === 'ended' && entry.valid_to && (
                            <span className="ml-1 text-muted-foreground">
                              {` (Promo ended ${new Date(entry.valid_to).toLocaleDateString()})`}
                            </span>
                          )}
                        </div>
                    <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <span>{entry.user_name || 'Anonymous'}</span>
                            {entry.reporter_trusted && (
                              <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
                                Trusted
                              </Badge>
                            )}
                          </div>
                          <span>{formatDate(entry.reported_at)}</span>
                          <div className="flex items-center gap-2 text-[11px]">
                            <button
                              type="button"
                              disabled={!token}
                              onClick={() => entry.id && flagReport(entry.id, 1)}
                              className={`inline-flex items-center gap-1 px-1 py-0.5 rounded ${
                                entry.my_vote === 1
                                  ? 'bg-emerald-600 text-emerald-50'
                                  : 'bg-background text-muted-foreground border border-border'
                              } ${!token ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <ThumbsUp className="w-3 h-3" />
                              <span>{entry.flags_up ?? 0}</span>
                            </button>
                            <button
                              type="button"
                              disabled={!token}
                              onClick={() => entry.id && flagReport(entry.id, -1)}
                              className={`inline-flex items-center gap-1 px-1 py-0.5 rounded ${
                                entry.my_vote === -1
                                  ? 'bg-red-600 text-red-50'
                                  : 'bg-background text-muted-foreground border border-border'
                              } ${!token ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <ThumbsDown className="w-3 h-3" />
                              <span>{entry.flags_down ?? 0}</span>
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-[11px] mt-1">
                            <button
                              type="button"
                              disabled={!token || entry.my_confirmed}
                              onClick={() => entry.id && confirmReport(entry.id)}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                                entry.my_confirmed
                                  ? 'bg-emerald-600 text-emerald-50'
                                  : 'bg-background text-muted-foreground border border-border'
                              } ${!token ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <CheckCircle2 className="w-3 h-3" />
                              <span>Still correct</span>
                            </button>
                            <span className="text-muted-foreground">
                              {entry.confirmations ?? 0} confirmation
                              {(entry.confirmations ?? 0) === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {loadingHistory && (
                      <div className="text-center text-[11px] text-muted-foreground">
                        Loading history...
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapIcon className="w-5 h-5 text-primary" />
                  <h4 className="font-heading font-bold text-foreground">Store Location</h4>
                </div>
                <div className="rounded-2xl overflow-hidden border border-border h-64">
                  <MapContainer
                    center={[selectedStore.latitude, selectedStore.longitude]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={[selectedStore.latitude, selectedStore.longitude]}>
                      <Popup>{selectedStore.store_name}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>

              {selectedStore.images && selectedStore.images.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-5 h-5 text-primary" />
                    <h4 className="font-heading font-bold text-foreground">Product Photos</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedStore.images.map((image, idx) => (
                      <img
                        key={idx}
                        src={image}
                        alt={`Product ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(image, '_blank')}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
